import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Hammer,
    LayoutGrid,
    List,
    Loader2,
    RefreshCw,
    Wallet,
} from "lucide-react";
import { toast } from "sonner";
import AiPageShell from "../../components/ai/AiPageShell";
import IcraKanban from "../../components/icra/IcraKanban";
import { icraMerkez, ICRA_DURUMLARI } from "../../lib/icraApi";

const TABS = [
    { key: "ozet", label: "Özet", icon: Hammer },
    { key: "kanban", label: "Kanban", icon: LayoutGrid },
    { key: "liste", label: "Liste", icon: List },
];

function StatCard({ label, value, accent, onClick, active }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-lg border px-4 py-3 min-w-[110px] text-left transition ${
                active
                    ? "border-orange-500/60 bg-orange-500/10"
                    : "border-white/10 bg-[#0B0F19]/80 hover:border-white/20"
            }`}
        >
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`text-xl font-semibold mt-1 ${accent || "text-slate-100"}`}>
                {value ?? "—"}
            </div>
        </button>
    );
}

function formatTl(val) {
    const n = Number(val);
    if (Number.isNaN(n)) return "—";
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
    }).format(n);
}

export default function IcraMerkezPage() {
    const [tab, setTab] = useState("ozet");
    const [ozet, setOzet] = useState(null);
    const [kanban, setKanban] = useState(null);
    const [liste, setListe] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [q, setQ] = useState("");

    const params = useMemo(() => {
        const p = { ...filters };
        if (q.trim()) p.q = q.trim();
        return p;
    }, [filters, q]);

    const loadOzet = useCallback(async () => {
        const data = await icraMerkez.ozet();
        setOzet(data);
    }, []);

    const loadKanban = useCallback(async () => {
        const data = await icraMerkez.kanban(params);
        setKanban(data);
    }, [params]);

    const loadListe = useCallback(async () => {
        const data = await icraMerkez.list({ ...params, page_size: 100 });
        setListe(Array.isArray(data) ? data : data.results || []);
    }, [params]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            await loadOzet();
            if (tab === "kanban") await loadKanban();
            if (tab === "liste") await loadListe();
        } catch {
            toast.error("Veri yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, [tab, loadOzet, loadKanban, loadListe]);

    useEffect(() => {
        refresh();
    }, [tab, params]);

    const setDurumFilter = (key) => {
        setFilters((f) => {
            const n = { ...f };
            delete n.dosya_durumu;
            delete n.tahsil_bekleyen;
            if (key === "tahsil_bekleyen") n.tahsil_bekleyen = "1";
            else if (key) n.dosya_durumu = key;
            return n;
        });
        setTab("liste");
    };

    return (
        <AiPageShell
            title="İcra Merkezi"
            subtitle="Takip, tahsilat ve ödeme entegrasyonu"
            icon={Hammer}
        >
            <div className="flex flex-wrap gap-2 mb-4">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border ${
                            tab === t.key
                                ? "bg-orange-600/30 border-orange-500/50 text-white"
                                : "border-white/10 text-slate-400 hover:text-white"
                        }`}
                    >
                        <t.icon className="h-4 w-4" />
                        {t.label}
                    </button>
                ))}
                <div className="flex-1" />
                <button
                    type="button"
                    onClick={refresh}
                    className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-white"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </button>
                <Link
                    to="/e/icralar"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-sm text-[#F3E5AB] hover:bg-[#D4AF37]/20"
                >
                    <List className="h-4 w-4" /> Veri girişi / Liste
                </Link>
                <Link
                    to="/e/odemeler"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-sm text-emerald-200 hover:bg-emerald-500/20"
                >
                    <Wallet className="h-4 w-4" /> Ödeme Takibi
                </Link>
            </div>

            <div className="flex flex-wrap gap-2 mb-4 items-end">
                <input
                    type="search"
                    placeholder="İcra no, dosya no, plaka…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white min-w-[200px]"
                />
                <select
                    value={filters.dosya_durumu || ""}
                    onChange={(e) =>
                        setFilters((f) => ({
                            ...f,
                            dosya_durumu: e.target.value || undefined,
                        }))
                    }
                    className="rounded-lg bg-[#0B0F19] border border-white/10 px-2 py-2 text-sm text-white"
                >
                    <option value="">Tüm durumlar</option>
                    {ICRA_DURUMLARI.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>

            {tab === "ozet" && ozet && (
                <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                        <StatCard label="Toplam dosya" value={ozet.toplam} />
                        <StatCard
                            label="Tahsil bekleyen"
                            value={ozet.tahsil_bekleyen}
                            accent="text-orange-300"
                            onClick={() => setDurumFilter("tahsil_bekleyen")}
                            active={filters.tahsil_bekleyen === "1"}
                        />
                        <StatCard
                            label="Tahsil edildi"
                            value={ozet.tahsil_edilen_dosya}
                            accent="text-emerald-300"
                            onClick={() => setDurumFilter("TAHSIL_EDILDI")}
                            active={filters.dosya_durumu === "TAHSIL_EDILDI"}
                        />
                        <StatCard label="Toplam borç" value={formatTl(ozet.toplam_borc)} />
                        <StatCard
                            label="Tahsil edilen"
                            value={formatTl(ozet.toplam_tahsil)}
                            accent="text-emerald-300"
                        />
                        <StatCard label="Net kazanç" value={formatTl(ozet.toplam_kazanc)} accent="text-[#F3E5AB]" />
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        {ICRA_DURUMLARI.map((d) => (
                            <button
                                key={d.value}
                                type="button"
                                onClick={() => setDurumFilter(d.value)}
                                className="rounded-lg border border-white/10 bg-[#121A2F] px-4 py-3 text-left hover:border-orange-500/30"
                            >
                                <div className="text-[10px] text-slate-500 uppercase">{d.label}</div>
                                <div className="text-lg font-semibold text-slate-100 mt-1">
                                    {ozet.durum?.[d.value] ?? 0}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {tab === "kanban" && (
                <IcraKanban
                    columns={kanban?.columns}
                    onRefresh={refresh}
                    loading={loading}
                />
            )}

            {tab === "liste" && (
                <div className="overflow-x-auto rounded-xl border border-white/10">
                    {loading ? (
                        <div className="flex justify-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                        </div>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="bg-[#121A2F] text-slate-500 uppercase">
                                <tr>
                                    <th className="p-2 text-left">İcra No</th>
                                    <th className="p-2 text-left">Dosya</th>
                                    <th className="p-2 text-left">Müvekkil</th>
                                    <th className="p-2 text-left">Durum</th>
                                    <th className="p-2 text-right">Borç</th>
                                    <th className="p-2 text-right">Tahsil</th>
                                    <th className="p-2 text-right">Kalan</th>
                                </tr>
                            </thead>
                            <tbody>
                                {liste.map((row) => (
                                    <tr key={row.id} className="border-t border-white/5 hover:bg-white/5">
                                        <td className="p-2">
                                            <Link
                                                to={`/e/icralar/${row.id}`}
                                                className="text-[#F3E5AB] font-mono hover:underline"
                                            >
                                                {row.icra_dosya_no || row.id}
                                            </Link>
                                        </td>
                                        <td className="p-2 font-mono text-slate-400">{row.dosya_no}</td>
                                        <td className="p-2 text-slate-300">{row.muvekkil_ad_soyad}</td>
                                        <td className="p-2 text-slate-300">
                                            {row.dosya_durumu_display || row.dosya_durumu}
                                        </td>
                                        <td className="p-2 text-right text-slate-200">{row.borc_tutari}</td>
                                        <td className="p-2 text-right text-emerald-300">
                                            {row.tahsil_edilen_tutar ?? 0}
                                        </td>
                                        <td className="p-2 text-right text-orange-300">
                                            {row.kalan_borc ?? "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </AiPageShell>
    );
}
